import getDomain from '../helpers/getDomain';
import axios from 'axios';
import React, { useState, createContext, useContext, useCallback } from 'react';
import { AlertContext } from './AlertContext';
import {
  getOrderToken,
  isOrderToken,
  removeOrderToken,
  setOrderToken,
} from '../helpers/orderToken';
import { getBasketToken, removeBasketToken } from '../helpers/basketToken';
import { BasketContext } from './BasketContext';

export const OrderContext = createContext();

export const OrderProvider = (props) => {
  const [order, updateOrder] = useState(null);
  const [, showAlert] = useContext(AlertContext);
  const [, , , , updateBasket] = useContext(BasketContext);

  const getHeader = useCallback(() => {
    return {
      Authorization: `Bearer ${getOrderToken()}`,
    };
  }, []);

  const errorHandler = useCallback(
    (error) => {
      if (error.response) {
        console.error(error.response.data);

        showAlert({
          status: 'warning',
          text: error.response.data.message ?? error.message,
        });
      } else if (error.request) {
        console.error(error.request);
      } else {
        console.error('Error', error.message);
      }

      //don't kill the token if a validation error
      if (error.response.status !== 422) {
        removeOrderToken();
        removeBasketToken();
        updateBasket({});
      }
    },
    [showAlert, updateBasket]
  );

  const getOrder = useCallback(
    async (successCallback, errorCallback) => {
      axios
        .get(getDomain() + '/api/order', {
          headers: getHeader(),
        })
        .then((response) => {
          updateOrder(response.data);

          if (successCallback) {
            successCallback();
          }
        })
        .catch((error) => {
          errorHandler(error);

          if (errorCallback) {
            errorCallback();
          }
        });
    },
    [errorHandler, getHeader]
  );

  const createNewOrder = useCallback(
    async (cb) => {
      if (isOrderToken()) {
        axios
          .post(
            getDomain() + '/api/order/update-lines',
            {},
            {
              headers: {
                Authorization: `Bearer ${getOrderToken()}`,
              },
            }
          )
          .then((response) => {
            updateOrder(response.data);
            cb();
          })
          .catch((error) => {
            errorHandler(error);
          });
      } else {
        axios
          .post(
            getDomain() + '/api/order/begin-checkout',
            {},
            {
              headers: {
                Authorization: `Bearer ${getBasketToken()}`,
              },
            }
          )
          .then((response) => {
            setOrderToken(response.data.token);

            getOrder(cb);
          })
          .catch((error) => {
            errorHandler(error);
          });
      }
    },
    [errorHandler, getOrder]
  );

  const completeOrder = useCallback(
    async (data, cb) => {
      updateOrder(data);

      axios
        .put(getDomain() + '/api/order/order-info', data, {
          headers: getHeader(),
        })
        .then((response) => {
          updateOrder(response.data);

          if (cb) {
            cb();
          }
        })
        .catch((error) => {
          errorHandler(error);
        });
    },
    [errorHandler, getHeader]
  );

  return (
    <OrderContext.Provider
      value={[order, createNewOrder, completeOrder, getOrder]}
    >
      {props.children}
    </OrderContext.Provider>
  );
};
